export interface AssessmentQuestion {
  id: string;
  text: string;
  category: string;
}

export interface AssessmentModule {
  id: string;
  name: string;
  description: string;
  questions: AssessmentQuestion[];
  program_slug: string;
  program_name: string;
}

export const LEVEL_A_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'q1',
    text: 'I\'ve tried to cut back or stop a behavior and couldn\'t.',
    category: 'control'
  },
  {
    id: 'q2',
    text: 'I keep doing something even when it harms me or others.',
    category: 'harm'
  },
  {
    id: 'q3',
    text: 'I hide or minimize a behavior from people close to me.',
    category: 'secrecy'
  },
  {
    id: 'q4',
    text: 'I spend a lot of time thinking about, planning, or recovering from a behavior.',
    category: 'obsession'
  },
  {
    id: 'q5',
    text: 'I need more (stronger, riskier, longer) of something to get the same relief.',
    category: 'tolerance'
  },
  {
    id: 'q6',
    text: 'I use a behavior to escape feelings or stress.',
    category: 'escape'
  },
  {
    id: 'q7',
    text: 'A behavior has hurt my relationships, work, or health.',
    category: 'consequences'
  }
];

export const ASSESSMENT_MODULES: AssessmentModule[] = [
  {
    id: 'alcohol',
    name: 'Alcohol',
    description: 'Questions about drinking patterns and alcohol-related problems',
    program_slug: 'aa',
    program_name: 'Alcoholics Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'Have you felt you should cut down but couldn\'t?',
        category: 'control'
      },
      {
        id: 'q2',
        text: 'Have you missed work or school due to drinking?',
        category: 'consequences'
      },
      {
        id: 'q3',
        text: 'Do you need a morning drink (eye-opener)?',
        category: 'dependence'
      },
      {
        id: 'q4',
        text: 'Do you keep drinking despite problems it causes?',
        category: 'harm'
      }
    ]
  },
  {
    id: 'drugs',
    name: 'Drugs',
    description: 'Questions about drug use and substance-related problems',
    program_slug: 'na',
    program_name: 'Narcotics Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'Do you use alone or avoid people who disapprove?',
        category: 'secrecy'
      },
      {
        id: 'q2',
        text: 'Do you substitute drugs thinking the problem was a specific one?',
        category: 'rationalization'
      },
      {
        id: 'q3',
        text: 'Do you manipulate a doctor or mix drugs to offset effects?',
        category: 'control'
      },
      {
        id: 'q4',
        text: 'Have you tried to stop or control and couldn\'t?',
        category: 'powerlessness'
      }
    ]
  },
  {
    id: 'sex',
    name: 'Sex & Pornography',
    description: 'Questions about sexual behavior and pornography use',
    program_slug: 'saa',
    program_name: 'Sex Addicts Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'My sexual behavior feels out of control.',
        category: 'control'
      },
      {
        id: 'q2',
        text: 'I\'ve made promises to stop or limit and failed.',
        category: 'powerlessness'
      },
      {
        id: 'q3',
        text: 'I hide my behavior and feel shame about it.',
        category: 'secrecy'
      },
      {
        id: 'q4',
        text: 'I continue even when it harms me or others.',
        category: 'harm'
      }
    ]
  },
  {
    id: 'food',
    name: 'Food & Eating',
    description: 'Questions about eating patterns and food-related problems',
    program_slug: 'oa',
    program_name: 'Overeaters Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'I eat more than planned or can\'t stop once I start.',
        category: 'control'
      },
      {
        id: 'q2',
        text: 'I eat to escape feelings or when I\'m not hungry.',
        category: 'escape'
      },
      {
        id: 'q3',
        text: 'Diets or rules don\'t stick long-term.',
        category: 'powerlessness'
      },
      {
        id: 'q4',
        text: 'I feel guilt, shame, or secrecy around food.',
        category: 'secrecy'
      }
    ]
  },
  {
    id: 'gambling',
    name: 'Gambling',
    description: 'Questions about gambling behavior and related problems',
    program_slug: 'ga',
    program_name: 'Gamblers Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'Do you gamble longer or for more money than planned?',
        category: 'control'
      },
      {
        id: 'q2',
        text: 'Have you tried to stop and couldn\'t?',
        category: 'powerlessness'
      },
      {
        id: 'q3',
        text: 'Have you lied or borrowed to cover losses or keep gambling?',
        category: 'secrecy'
      },
      {
        id: 'q4',
        text: 'Has gambling harmed your finances or relationships?',
        category: 'harm'
      }
    ]
  },
  {
    id: 'money',
    name: 'Money & Debt',
    description: 'Questions about spending patterns and financial problems',
    program_slug: 'da',
    program_name: 'Debtors Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'Do you spend or incur debt compulsively and can\'t keep commitments to stop?',
        category: 'control'
      },
      {
        id: 'q2',
        text: 'Do you hide financial behavior or do money issues disrupt work/relationships?',
        category: 'secrecy'
      }
    ]
  },
  {
    id: 'relationships',
    name: 'Relationships & Codependency',
    description: 'Questions about relationship patterns and codependent behaviors',
    program_slug: 'coda',
    program_name: 'Co-Dependents Anonymous',
    questions: [
      {
        id: 'q1',
        text: 'Do you repeatedly prioritize others over yourself to your detriment?',
        category: 'boundaries'
      },
      {
        id: 'q2',
        text: 'Do you struggle with setting and maintaining healthy boundaries?',
        category: 'boundaries'
      },
      {
        id: 'q3',
        text: 'Do you depend on others\' control or approval?',
        category: 'dependence'
      },
      {
        id: 'q4',
        text: 'Do you avoid or fear true intimacy in relationships?',
        category: 'intimacy'
      }
    ]
  }
];

export const PROGRAM_SLUGS = ASSESSMENT_MODULES.map(module => module.program_slug);

export const getModuleById = (id: string): AssessmentModule | undefined => {
  return ASSESSMENT_MODULES.find(module => module.id === id);
};

export const getProgramBySlug = (slug: string): AssessmentModule | undefined => {
  return ASSESSMENT_MODULES.find(module => module.program_slug === slug);
};
